import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Alert,
  CircularProgress,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Avatar,
  IconButton,
  LinearProgress,
  Container,
  Stack,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControl,
  InputLabel,
  Select,
  Tabs,
  Tab,
  Badge,
} from '@mui/material';
import {
  Search,
  Visibility,
  CheckCircle,
  Cancel,
  Warning,
  Error as ErrorIcon,
  Assessment,
  FilterList,
  Sort,
  ExpandMore,
  ThumbUp,
  ThumbDown,
  Flag,
  Security,
  Schedule,
  TrendingUp,
  Inventory,
  Analytics,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';

const StyledCard = styled(Card)(({ theme }) => ({
  transition: 'all 0.3s ease-in-out',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: theme.shadows[6],
  },
}));

const PriorityCard = styled(Card)<{ priority: string }>(({ theme, priority }) => ({
  borderLeft: `4px solid ${
    priority === 'high' ? theme.palette.error.main :
    priority === 'medium' ? theme.palette.warning.main :
    theme.palette.success.main
  }`,
}));

interface ReviewItem {
  id: number;
  type: 'product' | 'analysis';
  item_id: string;
  name: string;
  description: string;
  brand_name: string;
  category_name: string;
  status: string;
  risk_level: string;
  confidence_score: number;
  created_at: string;
  priority_score: number;
  primary_image_url?: string;
  is_counterfeit?: boolean;
  test_product_seller?: string;
  test_product_price?: number;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`review-tabpanel-${index}`}
      aria-labelledby={`review-tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

const ReviewQueuePage: React.FC = () => {
  const [reviewItems, setReviewItems] = useState<ReviewItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedItem, setSelectedItem] = useState<ReviewItem | null>(null);
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [reviewAction, setReviewAction] = useState<'approve' | 'reject' | null>(null);
  const [reviewNotes, setReviewNotes] = useState('');
  const [submittingReview, setSubmittingReview] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [riskFilter, setRiskFilter] = useState('');
  const [sortBy, setSortBy] = useState('created_at');
  const [tabValue, setTabValue] = useState(0);
  const [typeFilter, setTypeFilter] = useState<'all' | 'products' | 'analyses'>('all');
  const navigate = useNavigate();

  useEffect(() => {
    loadReviewQueue();
  }, [typeFilter]);

  const loadReviewQueue = async () => {
    try {
      setLoading(true);
      setError(null);

      const url = typeFilter === 'all' 
        ? 'https://server.bitsindia.in/api/review/queue/'
        : `https://server.bitsindia.in/api/review/queue/?type_filter=${typeFilter}`;

      const response = await fetch(url, {
        headers: {
          'Authorization': 'Bearer demo-token',
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setReviewItems(data.queue || []);
      } else {
        throw new Error('Failed to load review queue');
      }
    } catch (err: any) {
      console.error('Error loading review queue:', err);
      setError(err.message || 'Failed to load review queue');
      setReviewItems([]);
    } finally {
      setLoading(false);
    }
  };

  const handleReviewAction = async (item: ReviewItem, action: 'approve' | 'reject') => {
    setSelectedItem(item);
    setReviewAction(action);
    setReviewDialogOpen(true);
  };

  const submitReview = async () => {
    if (!selectedItem || !reviewAction) return;

    try {
      setSubmittingReview(true);

      let url: string;
      if (selectedItem.type === 'product') {
        url = `https://server.bitsindia.in/api/review/product/${selectedItem.item_id}/decision/`;
      } else {
        url = `https://server.bitsindia.in/api/review/${selectedItem.item_id}/decision/`;
      }

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': 'Bearer demo-token',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          decision: reviewAction === 'approve' ? 'approved' : 'rejected',
          confidence: 0.9,
          notes: reviewNotes,
          time_spent_minutes: 5,
          flagged_differences: []
        }),
      });

      if (response.ok) {
        // Remove item from queue
        setReviewItems(prev => prev.filter(item => item.id !== selectedItem.id));
        setReviewDialogOpen(false);
        setReviewNotes('');
        setSelectedItem(null);
        setReviewAction(null);
        
        // Show success message
        setError(`${selectedItem.type === 'product' ? 'Product' : 'Analysis'} ${reviewAction}d successfully!`);
        setTimeout(() => setError(null), 3000);
      } else {
        throw new Error(`Failed to ${reviewAction} ${selectedItem.type}`);
      }
    } catch (err: any) {
      console.error('Error submitting review:', err);
      setError(err.message || 'Failed to submit review');
    } finally {
      setSubmittingReview(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'pending_review': return 'warning';
      case 'approved': return 'success';
      case 'rejected': return 'error';
      default: return 'default';
    }
  };

  const getRiskColor = (risk: string) => {
    switch (risk.toLowerCase()) {
      case 'high': return 'error';
      case 'medium': return 'warning';
      case 'low': return 'success';
      default: return 'default';
    }
  };

  const getRiskIcon = (riskLevel: string) => {
    switch (riskLevel.toLowerCase()) {
      case 'high': return <ErrorIcon color="error" />;
      case 'medium': return <Warning color="warning" />;
      case 'low': return <CheckCircle color="success" />;
      default: return <Security />;
    }
  };

  const getPriorityScore = (item: ReviewItem) => {
    return item.priority_score;
  };

  const filteredItems = reviewItems
    .filter(item => {
      const matchesSearch = item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           (item.brand_name && item.brand_name.toLowerCase().includes(searchTerm.toLowerCase()));
      const matchesRisk = !riskFilter || item.risk_level === riskFilter;
      return matchesSearch && matchesRisk;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'priority':
          return getPriorityScore(b) - getPriorityScore(a);
        case 'confidence':
          return b.confidence_score - a.confidence_score;
        case 'risk':
          const riskOrder = { high: 3, medium: 2, low: 1 };
          return (riskOrder[b.risk_level as keyof typeof riskOrder] || 0) - 
                 (riskOrder[a.risk_level as keyof typeof riskOrder] || 0);
        case 'created_at':
        default:
          return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      }
    });

  const highPriorityItems = filteredItems.filter(item => 
    item.priority_score > 8.0
  );

  const pendingItems = filteredItems.filter(item => item.status === 'PENDING_REVIEW');

  const products = filteredItems.filter(item => item.type === 'product');
  const analyses = filteredItems.filter(item => item.type === 'analysis');

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          <CircularProgress size={60} />
          <Typography variant="h6" color="text.secondary">
            Loading review queue...
          </Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h3" component="h1" fontWeight="bold" color="primary" gutterBottom>
          Review Queue
        </Typography>
        <Typography variant="subtitle1" color="text.secondary">
          Review and approve products and counterfeit detection analyses
        </Typography>
      </Box>

      {/* Error/Success Alert */}
      {error && (
        <Alert
          severity={error.includes('successfully') ? 'success' : 'error'}
          sx={{ mb: 3 }}
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StyledCard>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ bgcolor: 'warning.main' }}>
                  <Schedule />
                </Avatar>
                <Box>
                  <Typography variant="h4" fontWeight="bold">
                    {pendingItems.length}
                  </Typography>
                  <Typography color="text.secondary">Pending Review</Typography>
                </Box>
              </Box>
            </CardContent>
          </StyledCard>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StyledCard>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ bgcolor: 'error.main' }}>
                  <Flag />
                </Avatar>
                <Box>
                  <Typography variant="h4" fontWeight="bold">
                    {highPriorityItems.length}
                  </Typography>
                  <Typography color="text.secondary">High Priority</Typography>
                </Box>
              </Box>
            </CardContent>
          </StyledCard>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StyledCard>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ bgcolor: 'primary.main' }}>
                  <Inventory />
                </Avatar>
                <Box>
                  <Typography variant="h4" fontWeight="bold">
                    {products.length}
                  </Typography>
                  <Typography color="text.secondary">Products Pending</Typography>
                </Box>
              </Box>
            </CardContent>
          </StyledCard>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StyledCard>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ bgcolor: 'info.main' }}>
                  <Analytics />
                </Avatar>
                <Box>
                  <Typography variant="h4" fontWeight="bold">
                    {analyses.length}
                  </Typography>
                  <Typography color="text.secondary">Analyses Pending</Typography>
                </Box>
              </Box>
            </CardContent>
          </StyledCard>
        </Grid>
      </Grid>

      {/* Type Filter Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={tabValue} onChange={handleTabChange} aria-label="review queue tabs">
          <Tab 
            label={
              <Badge badgeContent={filteredItems.length} color="primary">
                All Items
              </Badge>
            } 
            onClick={() => setTypeFilter('all')}
          />
          <Tab 
            label={
              <Badge badgeContent={products.length} color="primary">
                Products
              </Badge>
            } 
            onClick={() => setTypeFilter('products')}
          />
          <Tab 
            label={
              <Badge badgeContent={analyses.length} color="primary">
                Analyses
              </Badge>
            } 
            onClick={() => setTypeFilter('analyses')}
          />
        </Tabs>
      </Box>

      {/* Search and Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              placeholder="Search by name or brand..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />,
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Risk Level</InputLabel>
              <Select
                value={riskFilter}
                onChange={(e) => setRiskFilter(e.target.value)}
                label="Risk Level"
              >
                <MenuItem value="">All Risk Levels</MenuItem>
                <MenuItem value="HIGH">High Risk</MenuItem>
                <MenuItem value="MEDIUM">Medium Risk</MenuItem>
                <MenuItem value="LOW">Low Risk</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Sort By</InputLabel>
              <Select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                label="Sort By"
              >
                <MenuItem value="priority">Priority</MenuItem>
                <MenuItem value="confidence">Confidence</MenuItem>
                <MenuItem value="risk">Risk Level</MenuItem>
                <MenuItem value="created_at">Date Created</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <Button
              fullWidth
              variant="outlined"
              onClick={loadReviewQueue}
              startIcon={<FilterList />}
            >
              Refresh
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Review Items */}
      {filteredItems.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No items in review queue
          </Typography>
          <Typography variant="body2" color="text.secondary">
            All items have been reviewed or there are no pending reviews.
          </Typography>
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {filteredItems.map((item) => (
            <Grid item xs={12} key={`${item.type}-${item.id}`}>
              <PriorityCard priority={item.risk_level.toLowerCase()}>
                <CardContent>
                  <Grid container spacing={2} alignItems="center">
                    {/* Item Image */}
                    <Grid item xs={12} sm={2}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar sx={{ width: 60, height: 60 }}>
                          {item.type === 'product' ? <Inventory /> : <Analytics />}
                        </Avatar>
                        <Chip 
                          label={item.type.toUpperCase()} 
                          size="small" 
                          color={item.type === 'product' ? 'primary' : 'secondary'}
                        />
                      </Box>
                    </Grid>

                    {/* Item Details */}
                    <Grid item xs={12} sm={4}>
                      <Typography variant="h6" fontWeight="bold" gutterBottom>
                        {item.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        {item.description}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <Chip label={item.brand_name} size="small" variant="outlined" />
                        <Chip label={item.category_name} size="small" variant="outlined" />
                      </Box>
                    </Grid>

                    {/* Status and Risk */}
                    <Grid item xs={12} sm={2}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Chip
                          label={item.status.replace('_', ' ')}
                          color={getStatusColor(item.status) as any}
                          size="small"
                        />
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          {getRiskIcon(item.risk_level)}
                          <Typography variant="caption" color="text.secondary">
                            {item.risk_level} Risk
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>

                    {/* Priority and Confidence */}
                    <Grid item xs={12} sm={2}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Typography variant="body2" fontWeight="bold">
                          Priority: {item.priority_score.toFixed(1)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Confidence: {(item.confidence_score * 100).toFixed(0)}%
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {new Date(item.created_at).toLocaleDateString()}
                        </Typography>
                      </Box>
                    </Grid>

                    {/* Actions */}
                    <Grid item xs={12} sm={2}>
                      <Box sx={{ display: 'flex', gap: 1, flexDirection: 'column' }}>
                        {item.type === 'analysis' && (
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<Visibility />}
                            onClick={() => navigate(`/analyses/${item.item_id}`)}
                            title="View Analysis Details"
                            sx={{
                              minWidth: 80,
                              whiteSpace: 'nowrap',
                              textTransform: 'none',
                              fontSize: '0.75rem'
                            }}
                          >
                            View
                          </Button>
                        )}
                        <Button
                          size="small"
                          variant="contained"
                          color="success"
                          startIcon={<ThumbUp />}
                          onClick={() => handleReviewAction(item, 'approve')}
                          disabled={item.status !== 'PENDING_REVIEW'}
                          sx={{
                            minWidth: 80,
                            whiteSpace: 'nowrap',
                            textTransform: 'none',
                            fontSize: '0.75rem',
                            py: 0.5,
                            px: 1
                          }}
                        >
                          Approve
                        </Button>
                        <Button
                          size="small"
                          variant="contained"
                          color="error"
                          startIcon={<ThumbDown />}
                          onClick={() => handleReviewAction(item, 'reject')}
                          disabled={item.status !== 'PENDING_REVIEW'}
                          sx={{
                            minWidth: 80,
                            whiteSpace: 'nowrap',
                            textTransform: 'none',
                            fontSize: '0.75rem',
                            py: 0.5,
                            px: 1
                          }}
                        >
                          Reject
                        </Button>
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </PriorityCard>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Review Dialog */}
      <Dialog open={reviewDialogOpen} onClose={() => setReviewDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {reviewAction === 'approve' ? 'Approve' : 'Reject'} {selectedItem?.type === 'product' ? 'Product' : 'Analysis'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2 }}>
            <Typography variant="h6" gutterBottom>
              {selectedItem?.name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {selectedItem?.description}
            </Typography>
          </Box>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Review Notes"
            value={reviewNotes}
            onChange={(e) => setReviewNotes(e.target.value)}
            placeholder="Add your review notes here..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReviewDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={submitReview}
            variant="contained"
            color={reviewAction === 'approve' ? 'success' : 'error'}
            disabled={submittingReview}
          >
            {submittingReview ? <CircularProgress size={20} /> : `${reviewAction === 'approve' ? 'Approve' : 'Reject'}`}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ReviewQueuePage; 